import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import type { Account, VacationSettings, VacationState } from '@/api';
import { useApi } from '@/api/provider';
import { STALE_TIMES } from '@/api/queryClient';
import { queryKeys } from '@/api/queryKeys';
import { formatVacationDay, vacationPhase } from './vacation';
import './vacation-banner.css';

/**
 * "Out of office is on until …  · Turn off", above the message list.
 *
 * An automatic reply is the one setting that silently talks to other people
 * on the owner's behalf, so while it is on (or about to start) the mail view
 * says so, for every mailbox on screen: the active one, or each lane of the
 * unified list. It asks only mailboxes that can have one (`capabilities.sieve`)
 * and a failed read renders nothing — this is a reminder, not the settings
 * page, and an error here would be noise over the user's mail.
 *
 * A reply whose last day has passed is still `enabled` on the server (the date
 * test just stops matching), so the phase, not the switch, decides.
 */
export function VacationBanner({ accounts }: { accounts: Account[] }) {
  const api = useApi();
  const eligible = accounts.filter((account) => account.capabilities.sieve);
  const results = useQueries({
    queries: eligible.map((account) => ({
      queryKey: queryKeys.account(account.id).sieveVacation(),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.sieve.getVacation(account.id, signal),
      staleTime: STALE_TIMES.metadata,
      // A reminder is not worth a retry ladder.
      retry: false,
    })),
  });

  const showing = eligible.flatMap((account, index) => {
    const vacation = results[index]?.data?.vacation;
    if (vacation === null || vacation === undefined) return [];
    const phase = vacationPhase(vacation);
    return phase === 'active' || phase === 'scheduled' ? [{ account, vacation, phase }] : [];
  });
  if (showing.length === 0) return null;

  return (
    <>
      {showing.map(({ account, vacation, phase }) => (
        <VacationBannerRow
          key={account.id}
          account={account}
          vacation={vacation}
          phase={phase}
          named={eligible.length > 1}
        />
      ))}
    </>
  );
}

function VacationBannerRow({
  account,
  vacation,
  phase,
  named,
}: {
  account: Account;
  vacation: VacationSettings;
  phase: 'active' | 'scheduled';
  named: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const turnOff = useMutation({
    mutationFn: () =>
      api.sieve.putVacation(account.id, { vacation: { ...vacation, enabled: false } }),
    onSuccess: (next: VacationState) => {
      setError(null);
      queryClient.setQueryData(queryKeys.account(account.id).sieveVacation(), next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.account(account.id).sieveRules() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.account(account.id).sieveBackups() });
    },
    onError: (cause: unknown) =>
      setError(
        cause instanceof Error && cause.message !== ''
          ? cause.message
          : 'Out of office could not be turned off.',
      ),
  });

  const where = named ? ` for ${account.email}` : '';
  const text =
    phase === 'scheduled'
      ? `Out of office starts ${formatVacationDay(vacation.startDate)}${where}`
      : vacation.endDate !== ''
        ? `Out of office is on${where} until ${formatVacationDay(vacation.endDate)}`
        : `Out of office is on${where}`;

  return (
    <div className="vacation-banner" role="status" aria-label="Out of office">
      <span className="vacation-banner__text">{text}</span>
      <button
        type="button"
        className="btn btn--ghost"
        disabled={turnOff.isPending}
        onClick={() => turnOff.mutate()}
      >
        {turnOff.isPending ? 'Turning off…' : 'Turn off'}
      </button>
      <Link className="vacation-banner__link" to="/settings/out-of-office">
        Edit
      </Link>
      {error !== null ? (
        <span className="vacation-banner__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
